<script>
  import { modal } from '../lib/modal.js';
  import { createEventDispatcher } from 'svelte';
  import { Download, Upload, ShieldCheck, CircleAlert, FileSignature } from '../lib/icons/index.js';
  import { companyInvoicesApi } from '../stores/api.js';
  import { localizedError } from '../lib/labels.js';
  import { t } from '../i18n/index.js';
  import ErrorBanner from './ErrorBanner.svelte';

  /**
   * Signature-attachment failures whose server message is a technical English
   * sentence, so we say it in Italian instead. The code arrives in its own
   * field — `{ error, code }` — never inside the message.
   *
   * The certificate verdicts (untrusted_issuer, certificate_revoked,
   * revocation_unknown, trusted_list_unavailable, chain_unverifiable,
   * not_qualified) are deliberately absent: the server already answers those in
   * Italian, at a length and precision worth showing verbatim.
   */
  const KNOWN_SIGN_ERRORS = [
    'content_mismatch', 'already_signed', 'already_transmitted', 'not_cades',
    'no_original', 'not_outbound', 'empty_file', 'unreadable',
  ];

  export let companyId;
  export let invoiceId;
  /** Signature status from GET /invoices/:id/signature */
  export let status = null;

  const dispatch = createEventDispatcher();

  let downloading = false;
  let uploading = false;
  let downloaded = false;
  let error = null;
  let result = null;
  let selectedFile = null;
  let fileInput;

  // The user is guided through three steps. Downloading marks step 1 done;
  // choosing a file marks step 2 done. Nothing is enforced — someone who
  // already has the signed file can jump straight to the upload.
  $: step = result ? 4 : selectedFile ? 3 : downloaded ? 2 : 1;
  $: replacing = !!status?.attached;

  async function handleDownload() {
    downloading = true;
    error = null;
    try {
      const { blob, filename } = await companyInvoicesApi.downloadXml(companyId, invoiceId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      downloaded = true;
    } catch (err) {
      error = err;
    } finally {
      downloading = false;
    }
  }

  function handleFilePick(event) {
    error = null;
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.p7m')) {
      error = localizedError($t('signModal.errors.notP7m'));
      selectedFile = null;
      return;
    }
    selectedFile = file;
  }

  /** Read the file as base64 without the data: prefix. */
  function toBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
      reader.onerror = () => reject(localizedError($t('signModal.errors.unreadableFile')));
      reader.readAsDataURL(file);
    });
  }

  async function handleUpload() {
    if (!selectedFile) return;
    uploading = true;
    error = null;
    try {
      const content = await toBase64(selectedFile);
      result = await companyInvoicesApi.attachSigned(companyId, invoiceId, content, {
        replace: replacing,
      });
      dispatch('signed', result);
    } catch (err) {
      // The server returns the typed code in its own field — `{ error, code }`
      // — never inside the message, which is prose. Translate the codes a
      // normal user can act on; anything else keeps the server's own wording,
      // which is already written for the person reading it.
      error = KNOWN_SIGN_ERRORS.includes(err.code)
        ? localizedError($t(`signModal.errors.${err.code}`))
        : err;
    } finally {
      uploading = false;
    }
  }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
<div class="modal-backdrop" on:click|self={() => dispatch('close')}>
  <div class="modal-content w-full max-w-xl p-6" role="dialog" aria-modal="true" aria-labelledby="sign-modal-title"
    use:modal={{ onClose: () => (uploading ? null : dispatch('close')) }}>
    <h2 id="sign-modal-title" class="text-lg text-heading font-display mb-1 flex items-center gap-2">
      <FileSignature size={20} /> {$t('signModal.title')}
    </h2>

    {#if result}
      <!-- Done -->
      <div class="card p-4 my-4 border-green-500">
        <p class="font-bold text-success flex items-center gap-2">
          <ShieldCheck size={18} /> {$t('signModal.success.title')}
        </p>
        <p class="text-sm mt-2" style="color: var(--color-text-body);">
          {$t('signModal.success.body')}
        </p>
        <dl class="mt-3 space-y-1 text-xs text-muted">
          <div class="flex gap-2">
            <dt class="eyebrow">{$t('signModal.success.file')}:</dt>
            <dd class="font-mono break-all">{result.filename}</dd>
          </div>
          {#each result.signers || [] as signer}
            <div class="flex gap-2">
              <dt class="eyebrow">{$t('signModal.success.signer')}:</dt>
              <dd>{signer.commonName || signer.subject}</dd>
            </div>
          {/each}
        </dl>
      </div>
      <div class="flex justify-end pt-2">
        <button class="btn btn-primary btn-sm" on:click={() => dispatch('close')}>
          {$t('common.close')}
        </button>
      </div>
    {:else}
      <p class="text-sm mb-4" style="color: var(--color-text-body);">
        {$t('signModal.intro')}
      </p>

      {#if replacing}
        <div class="card p-3 mb-4 border-yellow-500">
          <p class="text-sm text-warning flex items-start gap-2">
            <CircleAlert size={16} class="mt-0.5 shrink-0" />
            <span>{$t('signModal.alreadySignedWarning')}</span>
          </p>
        </div>
      {/if}

      <ErrorBanner {error} />

      <ol class="space-y-4">
        <!-- Step 1: download -->
        <li class="card p-4 {step === 1 ? 'border-action' : ''}">
          <div class="flex items-start gap-3">
            <span class="step-num {step > 1 ? 'step-done' : ''}">1</span>
            <div class="flex-1">
              <h3 class="text-sm font-bold text-heading">{$t('signModal.step1.title')}</h3>
              <p class="text-xs text-muted mt-1">{$t('signModal.step1.help')}</p>
              <button
                class="btn btn-secondary btn-sm mt-3"
                on:click={handleDownload}
                disabled={downloading || !status?.xmlAvailable}
              >
                <Download size={14} class="mr-1" />
                {downloading ? $t('app.loading') : $t('signModal.step1.action')}
              </button>
              {#if status?.xmlFilename}
                <p class="text-xs text-muted mt-2 font-mono break-all">{status.xmlFilename}</p>
              {/if}
            </div>
          </div>
        </li>

        <!-- Step 2: sign externally -->
        <li class="card p-4 {step === 2 ? 'border-action' : ''}">
          <div class="flex items-start gap-3">
            <span class="step-num {step > 2 ? 'step-done' : ''}">2</span>
            <div class="flex-1">
              <h3 class="text-sm font-bold text-heading">{$t('signModal.step2.title')}</h3>
              <p class="text-xs text-muted mt-1">{$t('signModal.step2.help')}</p>
              <ul class="text-xs text-muted mt-2 list-disc list-inside space-y-1">
                <li>{$t('signModal.step2.pointCades')}</li>
                <li>{$t('signModal.step2.pointNoEdit')}</li>
              </ul>
            </div>
          </div>
        </li>

        <!-- Step 3: upload -->
        <li class="card p-4 {step >= 3 ? 'border-action' : ''}">
          <div class="flex items-start gap-3">
            <span class="step-num">3</span>
            <div class="flex-1">
              <h3 class="text-sm font-bold text-heading">{$t('signModal.step3.title')}</h3>
              <p class="text-xs text-muted mt-1">{$t('signModal.step3.help')}</p>

              <input
                bind:this={fileInput}
                type="file"
                accept=".p7m"
                class="hidden"
                on:change={handleFilePick}
              />
              <button class="btn btn-secondary btn-sm mt-3" on:click={() => fileInput.click()}>
                <Upload size={14} class="mr-1" /> {$t('signModal.step3.choose')}
              </button>

              {#if selectedFile}
                <p class="text-xs mt-2 font-mono break-all" style="color: var(--color-text-body);">
                  {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                </p>
              {/if}
            </div>
          </div>
        </li>
      </ol>

      <div class="flex justify-end gap-3 pt-5">
        <button class="btn btn-secondary btn-sm" on:click={() => dispatch('close')} disabled={uploading}>
          {$t('common.cancel')}
        </button>
        <button
          class="btn btn-primary btn-sm"
          on:click={handleUpload}
          disabled={uploading || !selectedFile}
        >
          <ShieldCheck size={14} class="mr-1" />
          {uploading ? $t('signModal.verifying') : $t('signModal.confirm')}
        </button>
      </div>
    {/if}
  </div>
</div>

<style>
  .step-num {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    flex-shrink: 0;
    border: 2px solid var(--color-border-default);
    color: var(--color-text-muted);
    font-weight: 700;
    font-size: 0.8rem;
  }
  .step-done {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  .hidden {
    display: none;
  }
</style>
